import { Box, Divider, Typography } from "@mui/material"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react"

import { DGText } from "../../components/common/DGText"
import { Footer } from "../../components/common/Footer"
import { licenses } from "../../generated/licenses"
import useIntl from "../../hooks/use-intl"
import { useRootPageTracking } from "../../hooks/use-root-page-tracking"
import useTranslate, { Namespaces } from "../../hooks/use-translate"
import { useEventBind } from "../../hooks/useEventBind"
import { appStore, useAuth } from "../../store/AppStore"
import { focusStore } from "../../store/FocusStore"
import {
  capitalizeFirstLetter,
  comb,
  CuratedContentSlugV2,
  s,
} from "../../utilities/common"
import { fontSizeFactor, keydown } from "../../utilities/constants"
import { theme, TI } from "../../utilities/theme"

import { AccessibilityModal } from "./AccessibilityModal"
import { SettingsButton } from "./components/SettingsButton"
import { DebugModal } from "./DebugModal"
import { InfoModal } from "./Info/InfoModal"
import { LanguageModal } from "./LanguageModal"
import { LoginModal } from "./Login/LoginModal"
import { PrivacySettingsModal } from "./PrivacySettingsModal"
import { SettingsFocusKeys } from "./settings-focus-keys"
import { StreamingModal } from "./StreamingModal"

const debugModeStorage = window.localStorage.getItem("debugMode") === "true"

export const SettingsScreen = ({ slug }: { slug: string }) => {
  const [loginOpen, setLoginOpen] = useState(false)
  const t = useTranslate()
  const t_common = useTranslate(Namespaces.Common)

  const { token, logout } = useAuth()
  const currentUser = appStore((state) => state.currentUser)
  const setPrompt = appStore((state) => state.setPrompt)
  const setSlug = appStore((state) => state.setSlug)
  const storeSlug = appStore((state) => state.slug)
  const setTabBarSlug = focusStore((state) => state.setTabBarSlug)
  const [debugMode, setDebugMode] = useState(debugModeStorage)

  const debugCounter = useRef(0)
  const debugPin = "31425"

  useEventBind<KeyboardEvent>(window, keydown, (e) => {
    if (debugMode) return
    if (debugPin[debugCounter.current] === (e.keyCode - 48).toString())
      debugCounter.current++
    else debugCounter.current = 0
    if (debugCounter.current >= 5) {
      window.localStorage.setItem("debugMode", "true")
      setDebugMode(true)
    }
  })

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    setVisible(slug === storeSlug)
  }, [storeSlug])

  useRootPageTracking(visible, capitalizeFirstLetter(slug))

  const { ref, focusKey } = useFocusable({
    focusable: visible,
    isFocusBoundary: true,
    focusBoundaryDirections: ["left", "right", "down"],
    saveLastFocusedChild: false,
  })
  const [privacyOpen, setPrivacyOpen] = useState(false)
  const [debugOpen, setDebugOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [streamingOpen, setStreamingOpen] = useState(false)
  const [infoModalOpen, setInfoModalOpen] = useState(false)
  const [accessibilityModalOpen, setAccessibilityModalOpen] = useState(false)

  const [infoTitle, setInfoTitle] = useState("")
  const [infoContent, setInfoContent] = useState("")
  const [returnFocusKey, setReturnFocusKey] = useState<string>()
  const setSpinner = appStore((state) => state.setSpinner)

  const scrollRef = useRef(document.getElementById("root"))

  const { dateFormat } = useIntl()

  useEffect(() => {
    if (visible) setSpinner("out")
  }, [visible])

  const userAriaLabel = useMemo(
    () =>
      currentUser?.id
        ? `${t_common("auth__logged_in_as").replace("<email />", "")} ${currentUser.email}. ${
            currentUser.ticket?.validUntil
              ? t("subscription__label_renews_on", [
                  dateFormat(new Date(currentUser.ticket.validUntil), "short"),
                ])
              : t("subscription__no_subscription_subtitle")
          }`
        : "",
    [currentUser],
  )

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <FocusContext.Provider value={focusKey}>
        <Box
          sx={{
            display: "flex",
            width: s("1140px"),
            flexDirection: "column",
            alignItems: "flex-start",
            color: "white",
            mt: s("53px"),
          }}
        >
          <DGText tx={TI.H05tv}>{t("navigation__settings")}</DGText>

          {currentUser && (
            <Box sx={{ mt: s("64px") }}>
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: s("36px", false, fontSizeFactor),
                  lineHeight: s("48px", false, fontSizeFactor),
                }}
              >
                {currentUser.email}
              </Typography>
              <DGText
                tx={TI.Subheadline05tv}
                sx={{
                  color: theme.palette.white[50],
                  mt: s("16px"),
                }}
              >
                {currentUser.ticket?.validUntil
                  ? t("subscription__label_renews_on", [
                      dateFormat(
                        new Date(currentUser.ticket.validUntil),
                        "short",
                      ),
                    ])
                  : t("subscription__no_subscription_subtitle")}
              </DGText>
            </Box>
          )}

          <FocusContext.Provider value={focusKey}>
            <Box ref={ref} sx={{ width: "100%" }}>
              <Section title={t("settings__account")}>
                {token ? (
                  <SettingsButton
                    scrollRef={scrollRef}
                    ariaLabel={`${t("navigation__logout")}. ${userAriaLabel}`}
                    title={t("navigation__logout")}
                    action={() => {
                      setPrompt({
                        confirmButtonText: t("settings__logout"),
                        dismissButtonText: t("settings__button_cancel"),
                        message: t("alert_message__logout"),
                        onConfirm: () => {
                          logout()
                          setFocus(SettingsFocusKeys.Login)
                        },
                        dismissible: true,
                        onClose: () => setFocus(SettingsFocusKeys.Login),
                        open: true,
                        title: t("alert_title__logout"),
                      })
                    }}
                    focusKey={SettingsFocusKeys.Login}
                  />
                ) : (
                  <SettingsButton
                    scrollRef={scrollRef}
                    title={t("navigation__login")}
                    action={() => setLoginOpen(true)}
                    focusKey={SettingsFocusKeys.Login}
                  />
                )}
              </Section>
              <Section title={t("navigation__settings")}>
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("settings__language")}
                  action={() => setLanguageOpen(true)}
                  focusKey={SettingsFocusKeys.Language}
                />
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t_common("settings__streaming")}
                  action={() => setStreamingOpen(true)}
                  focusKey={SettingsFocusKeys.Streaming}
                />
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("settings_menu_section_accessibility")}
                  action={() => setAccessibilityModalOpen(true)}
                  focusKey={SettingsFocusKeys.Accessibility}
                />
                {debugMode && (
                  <SettingsButton
                    scrollRef={scrollRef}
                    title={"Debug"}
                    action={() => setDebugOpen(true)}
                    focusKey={SettingsFocusKeys.DebugMenu}
                  />
                )}
              </Section>
              <Section title={t("settings__info")}>
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("settings__imprint")}
                  action={() => {
                    setInfoTitle(t("settings__imprint"))
                    setInfoContent(t("page__imprint_content"))
                    setReturnFocusKey(SettingsFocusKeys.Imprint)
                    setInfoModalOpen(true)
                  }}
                  focusKey={SettingsFocusKeys.Imprint}
                />
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("settings__terms")}
                  action={() => {
                    setInfoTitle(t("settings__terms"))
                    setInfoContent(t("page__terms_content"))
                    setReturnFocusKey(SettingsFocusKeys.Terms)
                    setInfoModalOpen(true)
                  }}
                  focusKey={SettingsFocusKeys.Terms}
                />
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("settings__privacy")}
                  action={() => {
                    setInfoTitle(t("settings__privacy"))
                    setInfoContent(t("page__privacy_content"))
                    setReturnFocusKey(SettingsFocusKeys.PrivacyPolicy)
                    setInfoModalOpen(true)
                  }}
                  focusKey={SettingsFocusKeys.PrivacyPolicy}
                />
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("settings__data_privacy")}
                  action={() => setPrivacyOpen(true)}
                  focusKey={SettingsFocusKeys.PrivacySettings}
                />
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("page__payment_provider_title")}
                  action={() => {
                    setInfoTitle(t("page__payment_provider_title"))
                    setInfoContent(t("page__payment_provider_content"))
                    setReturnFocusKey(SettingsFocusKeys.AdditionalDataInfo)
                    setInfoModalOpen(true)
                  }}
                  focusKey={SettingsFocusKeys.AdditionalDataInfo}
                />
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("page__accessibility_title")}
                  action={() => {
                    setInfoTitle(t("page__accessibility_title"))
                    setInfoContent(t("page__accessibility_content"))
                    setReturnFocusKey(SettingsFocusKeys.AccessibilityInfo)
                    setInfoModalOpen(true)
                  }}
                  focusKey={SettingsFocusKeys.AccessibilityInfo}
                />
                <SettingsButton
                  scrollRef={scrollRef}
                  title={t("settings__open_source_licences")}
                  action={() => {
                    setSpinner("in")
                    setTimeout(() => {
                      setInfoTitle(t("settings__open_source_licences"))
                      setInfoContent(licenses)
                      setReturnFocusKey(SettingsFocusKeys.OpenSourceLicences)
                      setInfoModalOpen(true)
                    }, 100)
                  }}
                  focusKey={SettingsFocusKeys.OpenSourceLicences}
                />
              </Section>
            </Box>
          </FocusContext.Provider>
        </Box>
        <Footer />
        <InfoModal
          open={comb.ine(infoModalOpen, setInfoModalOpen)}
          content={infoContent}
          title={infoTitle}
          returnFocusKey={returnFocusKey}
        />
        <AccessibilityModal
          open={comb.ine(accessibilityModalOpen, setAccessibilityModalOpen)}
          returnFocus={SettingsFocusKeys.Accessibility}
        />
        <LoginModal
          isOpen={loginOpen}
          close={() => {
            setLoginOpen(false)
            setFocus("SETTINGS:LOGIN")
          }}
          onSuccess={() => {
            setLoginOpen(false)
            setSlug(CuratedContentSlugV2.Discover)
            setTabBarSlug(CuratedContentSlugV2.Discover)
            setFocus("TABBAR:" + CuratedContentSlugV2.Discover)
          }}
        />
        <PrivacySettingsModal
          open={comb.ine(privacyOpen, setPrivacyOpen)}
          returnFocus={SettingsFocusKeys.PrivacySettings}
        />
        <StreamingModal
          open={comb.ine(streamingOpen, setStreamingOpen)}
          returnFocus={SettingsFocusKeys.Streaming}
        />
        <LanguageModal
          open={comb.ine(languageOpen, setLanguageOpen)}
          returnFocus={SettingsFocusKeys.Language}
        />
        {debugMode && (
          <DebugModal
            open={comb.ine(debugOpen, setDebugOpen)}
            returnFocus={SettingsFocusKeys.DebugMenu}
          />
        )}
      </FocusContext.Provider>
    </Box>
  )
}

const Section = ({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) => {
  return (
    <Box sx={{ mt: s("96px"), width: "100%" }}>
      <Typography
        sx={{
          textTransform: "uppercase",
          fontWeight: 400,
          fontSize: s("30px", false, fontSizeFactor),
          lineHeight: s("40px", false, fontSizeFactor),
          color: theme.palette.white[55],
        }}
      >
        {title}
      </Typography>
      <Divider sx={{ mt: s("16px"), borderColor: theme.palette.white[20] }} />
      {children}
    </Box>
  )
}
